import React, { useState, useEffect } from 'react'
import {
    Modal,
    Box,
    Typography,
    IconButton,
    Chip,
    Divider,
    ListItem,
} from '@mui/material'
import {
    Close as CloseIcon,
    NewReleases as NewReleasesIcon,
    Edit as EditIcon,
    Save as SaveIcon,
    Cancel as CancelIcon,
} from '@mui/icons-material'
import { styled } from '@mui/material/styles'
import { Editor, EditorProvider } from 'react-simple-wysiwyg';
import WysiwygEditorToolbar from './WysiwygEditorToolbar';
import Button from '@material-ui/core/Button'
import releaseNotesData from '../../../data/releaseNotes.json';
import config from '../../../config/runtime'

const StyledModal = styled(Modal)({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
})

const ModalContent = styled(Box)(({ theme }) => ({
    position: 'relative',
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    outline: 'none',
    padding: theme.spacing(4),
    maxWidth: '600px',
    width: '90%',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
    '&::-webkit-scrollbar': {
        width: '8px',
    },
    '&::-webkit-scrollbar-track': {
        background: '#f1f1f1',
        borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb': {
        background: '#888',
        borderRadius: '4px',
    },
}))

const VersionChip = styled(Chip)(({ theme }) => ({
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    fontWeight: 'bold',
    fontSize: '0.9rem',
    height: '28px',
}))

const Feature = styled(ListItem)(({ theme }) => ({
    backgroundColor: 'rgba(0, 0, 0, 0.02)',
    borderRadius: '8px',
    marginBottom: theme.spacing(1),
    transition: 'all 0.2s ease',
    '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.04)',
        transform: 'translateX(4px)',
    },
}))

const ContentDisplay = styled(Box)(({ theme }) => ({
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    padding: theme.spacing(3),
    backgroundColor: '#fafafa',
    minHeight: '200px',
    '& h1, & h2, & h3, & h4, & h5, & h6': {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(1),
    },
    '& p': {
        marginBottom: theme.spacing(1),
    },
    '& ul, & ol': {
        paddingLeft: theme.spacing(3),
    },
}))

interface ReleaseNotesModalProps {
    open: boolean
    onClose: () => void
}

const ReleaseNotesModal: React.FC<ReleaseNotesModalProps> = ({
    open,
    onClose,
}) => {
    const [contentEditableValue, setContentEditableValue] = useState(releaseNotesData.content);
    const [isEditMode, setIsEditMode] = useState(false);
    const [originalContent, setOriginalContent] = useState(releaseNotesData.content);

    useEffect(()=> {
        if (isEditMode) {
            localStorage.setItem("releaseNote", contentEditableValue);
        }
    }, [contentEditableValue, isEditMode]);

    const loadEditorData = () => {
        const savedNote = localStorage.getItem("releaseNote");
        if (savedNote) {
            setContentEditableValue(savedNote);
        } else {
            setContentEditableValue(releaseNotesData.content);
        }
        setOriginalContent(releaseNotesData.content);
    };

    useEffect(() => {
        loadEditorData();
    }, []);

    const handleEditClick = () => {
        setIsEditMode(true);
        setOriginalContent(contentEditableValue); // Store current content as backup
    };

    const handleCancelEdit = () => {
        setContentEditableValue(originalContent); // Restore original content
        setIsEditMode(false);
    };

    const handleSave = async () => {
        try {
            const dataToSave = {
                content: contentEditableValue,
                lastModified: new Date().toISOString()
            };

            const jsonString = JSON.stringify(dataToSave, null, 2);

            await navigator.clipboard.writeText(jsonString);

            // Also save to localStorage
            localStorage.setItem("releaseNote", contentEditableValue);

            setIsEditMode(false);
            setOriginalContent(contentEditableValue); // Update the backup

            alert("Release notes copied to clipboard! Paste into src/data/releaseNotes.json");
            console.log("Content copied to clipboard");
        } catch (error) {
            console.error("Error copying to clipboard:", error);
            // Fallback: show the content in console
            console.log("Copy this content to src/data/releaseNotes.json:", {
                content: contentEditableValue,
                lastModified: new Date().toISOString()
            });
        }
    };

    const createMarkup = (htmlContent: string) => {
        return { __html: htmlContent };
    };

    return (
        <StyledModal
            open={open}
            onClose={onClose}
            BackdropProps={{
                style: { backgroundColor: 'rgba(0, 0, 0, 0.6)' },
            }}
        >
            <ModalContent>
                {/* Close Button */}
                <IconButton
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: 'grey.500',
                    }}
                    onClick={onClose}
                >
                    <CloseIcon />
                </IconButton>

                {!isEditMode && config.NODE_ENV === 'development' && (
                    <IconButton
                        sx={{
                            position: 'absolute',
                            right: 48,
                            top: 8,
                            color: 'primary.main',
                        }}
                        onClick={handleEditClick}
                        title="Edit release notes"
                    >
                        <EditIcon />
                    </IconButton>
                )}

                {/* Header */}
                <Box
                    sx={{
                        mb: 3,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                    }}
                >
                    <NewReleasesIcon sx={{ fontSize: 32, color: '#4a287d' }} />
                    <Typography
                        variant="h5"
                        component="h2"
                        sx={{ fontWeight: 600 }}
                    >
                        What's New
                    </Typography>
                    {isEditMode && (
                        <Chip
                            label="Editing"
                            color="primary"
                            size="small"
                            sx={{ ml: 1 }}
                        />
                    )}
                </Box>

                {/* Version Info */}
                <Box
                    sx={{
                        mb: 3,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                    }}
                >
                    <VersionChip
                        label={`Web Application: ${config.BUILD_VERSION}`}
                        sx={{ backgroundColor: '#4a287d' }}
                    />
                    {
                        releaseNotesData.lastModified && (
                            <Typography
                                variant="caption"
                                sx={{ color: 'text.secondary' }}
                            >
                                Released: {new Date(releaseNotesData.lastModified).toLocaleDateString()}
                            </Typography>
                        )
                    }
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Content Area - Show either display or editor */}
                <Box
                    sx={{
                        mt: "2rem",
                        minHeight: isEditMode ? `50vh` : 'auto',
                        width: "100%",
                    }}
                >
                    {isEditMode ? (
                        <Box
                            sx={{
                                height: `50vh`,
                                display: 'flex',
                                alignItems: 'center',
                                mx: "auto"
                            }}
                        >
                            <EditorProvider>
                                <Editor
                                    autoFocus={true}
                                    autoSave="true"
                                    containerProps={{
                                        style: {
                                            height: "100%",
                                            width: '100%',
                                            resize: 'none',
                                            overflowY: 'auto',
                                            border: '1px solid',
                                            borderColor: '#ccc',
                                            borderRadius: '12px',
                                            padding: '8px',
                                        },
                                    }}
                                    placeholder="Edit release notes..."
                                    value={contentEditableValue}
                                    onChange={(e: any) => {
                                        const value = e.target.value;
                                        setContentEditableValue(value);
                                    }}
                                >
                                    <WysiwygEditorToolbar onAddAttachment={undefined} />
                                </Editor>
                            </EditorProvider>
                        </Box>
                    ) : (
                        <ContentDisplay>
                            <div dangerouslySetInnerHTML={createMarkup(contentEditableValue)} />
                        </ContentDisplay>
                    )}
                </Box>

                {/* Action Buttons */}
                {isEditMode ? (
                    <Box
                        sx={{
                            mt: "1rem",
                            display: "flex",
                            justifyContent: "space-between"
                        }}
                    >
                        <Button
                            variant="outlined"
                            startIcon={<CancelIcon />}
                            onClick={handleCancelEdit}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<SaveIcon />}
                            className={'primary-button'}
                            onClick={handleSave}
                        >
                            Save
                        </Button>
                    </Box>
                ) : (
                    // Only show the edit button in development
                    config.NODE_ENV === 'development' && (
                        <Box
                            sx={{
                                mt: "1rem",
                                display: "flex",
                                justifyContent: "center"
                            }}
                        >
                            <Button
                                variant="outlined"
                                startIcon={<EditIcon />}
                                onClick={handleEditClick}
                            >
                                Edit Release Notes
                            </Button>
                        </Box>
                    )
                )}

                {/* Footer */}
                <Box
                    sx={{
                        mt: 3,
                        pt: 2,
                        borderTop: '1px solid',
                        borderColor: 'divider',
                    }}
                >
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        align="center"
                    >
                        Thank you for using our application. For support,
                        contact our team.
                    </Typography>
                </Box>
            </ModalContent>
        </StyledModal>
    )
}

export default ReleaseNotesModal
